import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Store, Loader2, Check, ArrowLeft, Mail, Phone, FileText, Type, List, ToggleLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api, { settingsApi } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const PartnerApplicationPage = () => {
  const [formConfig, setFormConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Load form configuration on mount
  useEffect(() => {
    const loadFormConfig = async () => {
      try {
        const response = await settingsApi.getPartnerForm();
        if (response.data && response.data.fields) {
          setFormConfig(response.data);
          // Initialize form data with empty values for each field
          const initialData = {};
          response.data.fields.forEach(field => {
            const fieldKey = field.id || field.name;
            initialData[fieldKey] = field.type === 'checkbox' ? false : '';
          });
          setFormData(initialData);
        }
      } catch (error) {
        console.error('Error loading form config:', error);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadFormConfig();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  // Helper to find field value by label patterns
  const findFieldValue = (patterns) => {
    if (!formConfig?.fields) return '';
    for (const field of formConfig.fields) {
      const fieldKey = field.id || field.name;
      const labelLower = field.label?.toLowerCase() || '';
      if (patterns.some(p => labelLower.includes(p.toLowerCase()))) {
        return formData[fieldKey] || '';
      }
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields using field.id
    if (formConfig?.fields) {
      const missingRequired = formConfig.fields.filter(field => {
        const fieldKey = field.id || field.name;
        return field.required && !formData[fieldKey];
      });
      if (missingRequired.length > 0) {
        toast.error(`Completá los campos requeridos: ${missingRequired.map(f => f.label).join(', ')}`);
        return;
      }
    }
    
    setSubmitting(true);
    
    try {
      // Transform form data to include field labels for admin review
      const applicationData = {
        form_responses: Object.entries(formData).map(([key, value]) => {
          const field = formConfig?.fields?.find(f => (f.id || f.name) === key);
          return {
            field_id: key,
            field_label: field?.label || key,
            field_type: field?.type || 'text',
            value: value
          };
        }),
        // Extract key fields for the partner record
        business_name: findFieldValue(['nombre del negocio', 'nombre', 'business_name', 'name']) || 'Sin nombre',
        email: findFieldValue(['email', 'correo', 'e-mail']) || null,
        phone: findFieldValue(['teléfono', 'telefono', 'phone', 'celular']) || '',
        contact_name: findFieldValue(['nombre contacto', 'responsable', 'contact_name']) || '',
        category: findFieldValue(['categoría', 'categoria', 'category', 'rubro']) || 'otros'
      };
      
      await api.post('/partners/apply', applicationData);
      setSubmitted(true);
      toast.success('¡Solicitud enviada exitosamente!');
    } catch (error) {
      console.error('Error:', error);
      // Handle validation errors (array of error objects)
      let errorMessage = 'Error al enviar la solicitud. Intenta nuevamente.';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const getFieldIcon = (type) => {
    switch (type) {
      case 'email': return Mail;
      case 'phone': return Phone;
      case 'textarea': return FileText;
      case 'select': return List;
      case 'checkbox': return ToggleLeft;
      default: return Type;
    }
  };

  const renderField = (field) => {
    const Icon = getFieldIcon(field.type);
    const fieldKey = field.id || field.name;
    
    switch (field.type) {
      case 'textarea':
        return (
          <div key={fieldKey}>
            <label className="block text-sm text-[#A1A1AA] mb-2">
              {field.label} {field.required && <span className="text-[#D4AF37]">*</span>}
            </label>
            <textarea
              name={fieldKey}
              value={formData[fieldKey] || ''}
              onChange={handleChange}
              className="w-full bg-[#18181B] border border-[#27272A] rounded-lg px-4 py-3 text-white placeholder-[#52525B] focus:border-[#D4AF37] focus:outline-none transition-colors"
              rows={4}
              placeholder={field.placeholder || ''}
              required={field.required}
            />
          </div>
        );
      
      case 'select':
        return (
          <div key={fieldKey}>
            <label className="block text-sm text-[#A1A1AA] mb-2">
              {field.label} {field.required && <span className="text-[#D4AF37]">*</span>}
            </label>
            <select
              name={fieldKey}
              value={formData[fieldKey] || ''}
              onChange={handleChange}
              className="w-full bg-[#18181B] border border-[#27272A] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] focus:outline-none transition-colors"
              required={field.required}
            >
              <option value="">Seleccionar...</option>
              {field.options?.map((opt, i) => (
                <option key={i} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );
      
      case 'checkbox':
        return (
          <div key={fieldKey} className="flex items-start gap-3">
            <input
              type="checkbox"
              name={fieldKey}
              checked={formData[fieldKey] || false}
              onChange={handleChange}
              className="mt-1 w-5 h-5 rounded border-[#27272A] bg-[#18181B] text-[#D4AF37] focus:ring-[#D4AF37]"
              required={field.required}
            />
            <label className="text-sm text-[#A1A1AA]">
              {field.label} {field.required && <span className="text-[#D4AF37]">*</span>}
            </label>
          </div>
        );
      
      default:
        return (
          <div key={fieldKey}>
            <label className="block text-sm text-[#A1A1AA] mb-2">
              {field.label} {field.required && <span className="text-[#D4AF37]">*</span>}
            </label>
            <div className="relative">
              <Icon className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]" size={18} />
              <input
                type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
                name={fieldKey}
                value={formData[fieldKey] || ''}
                onChange={handleChange}
                className="w-full bg-[#18181B] border border-[#27272A] rounded-lg pl-12 pr-4 py-3 text-white placeholder-[#52525B] focus:border-[#D4AF37] focus:outline-none transition-colors"
                placeholder={field.placeholder || ''}
                required={field.required}
              />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <SEO 
        title="Quiero ser Partner - Bravo Producciones"
        description="Unite como partner de Bravo Producciones y accedé a beneficios exclusivos para tu negocio."
      />
      <Navbar />

      <main className="pt-24 md:pt-32 pb-16 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={16} />
            Volver al inicio
          </Link>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-10"
          >
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#D4AF37]/20 to-[#F59E0B]/20 border border-[#D4AF37]/30 rounded-2xl flex items-center justify-center mb-6">
              <Store size={40} className="text-[#D4AF37]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-black uppercase tracking-tight mb-3">
              Quiero ser Partner
            </h1>
            <p className="text-[#A1A1AA] text-lg max-w-md mx-auto">
              Unite a la red de partners de BRAVO PRODUCCIONES y hacé crecer tu negocio
            </p>
          </motion.div>

          {/* Success State */}
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#121212] border border-[#27272A] rounded-2xl p-8 text-center"
            >
              <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                <Check size={40} className="text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">¡Solicitud Enviada!</h2>
              <p className="text-[#A1A1AA] mb-8 max-w-sm mx-auto">
                Gracias por tu interés en ser parte de nuestra red de partners. 
                Nos pondremos en contacto contigo muy pronto.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold uppercase tracking-wider rounded-lg"
              >
                Volver al inicio
              </Link>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-[#121212] border border-[#27272A] rounded-2xl p-6 md:p-8"
            >
              {loadingConfig ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
                </div>
              ) : formConfig && formConfig.fields?.length > 0 ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {formConfig.fields.map(field => renderField(field))}
                  
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] text-black font-bold uppercase tracking-wider rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                  >
                    {submitting ? (
                      <Loader2 size={20} className="animate-spin" />
                    ) : (
                      <>
                        Enviar Solicitud
                      </>
                    )}
                  </button>
                </form>
              ) : (
                <div className="text-center py-12">
                  <FileText className="mx-auto text-zinc-600 mb-4" size={48} />
                  <p className="text-zinc-400">
                    El formulario de solicitud aún no está configurado.
                  </p>
                  <p className="text-zinc-500 text-sm mt-2">
                    Por favor, contactanos directamente para más información.
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PartnerApplicationPage;
